// src/components/admin/AdminBilling.jsx
import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

function moneyToCents(v) {
  const n = String(v).replace(/[^\d.,]/g, "").replace(",", ".");
  const f = parseFloat(n || "0");
  return Math.round(f * 100);
}

const Box = ({ title, children }) => (
  <div className="border rounded-2xl p-6 bg-white">
    <h3 className="text-lg font-semibold mb-4">{title}</h3>
    {children}
  </div>
);

export default function AdminBilling() {
  const [loading, setLoading] = useState(true);

  // Sources
  const [tickets, setTickets] = useState([]);
  const [experts, setExperts] = useState([]);
  const [partners, setPartners] = useState([]);

  // Forms
  const [payout, setPayout] = useState({
    ticket_id: "",
    expert_id: "",
    amount: "",
    scheduled_for: "",
    note: ""
  });

  const [invoice, setInvoice] = useState({
    ticket_id: "",
    partner_id: "",
    description: "",
    amount: "",
    due_date: ""
  });

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      // Tickets récents
      const { data: t1 } = await supabase
        .from("support_tickets")
        .select("id, subject, kind, status, requester_id, created_at, meta")
        .order("created_at", { ascending: false })
        .limit(200);

      // Experts d’estimation
      const { data: ex } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, company, partner_type, role")
        .eq("role", "expert")
        .or("partner_type.is.null,partner_type.eq.expert_valuation");

      // Partenaires transport/assurance
      const { data: pa } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, company, partner_type, role")
        .eq("role", "expert")
        .in("partner_type", ["transport", "insurance"]);

      setTickets(t1 || []);
      setExperts(ex || []);
      setPartners(pa || []);
      setLoading(false);
    };
    load();
  }, []);

  const estTickets = useMemo(
    () =>
      tickets.filter((t) =>
        ["estimation_request", "estimation_validation"].includes(t.kind)
      ),
    [tickets]
  );

  const partnerTickets = useMemo(
    () => tickets.filter((t) => ["transport", "insurance", "partner_request"].includes(t.kind)),
    [tickets]
  );

  const onCreatePayout = async (e) => {
    e.preventDefault();
    const cents = moneyToCents(payout.amount);
    if (!payout.expert_id || !cents) return;

    const { error } = await supabase.from("expert_payouts").insert({
      expert_id: payout.expert_id,
      ticket_id: payout.ticket_id || null,
      amount_cents: cents,
      currency: "EUR",
      status: "scheduled",
      scheduled_for: payout.scheduled_for || null,
      meta: { ticket_id: payout.ticket_id || null, note: payout.note || null }
    });

    if (!error) {
      setPayout({ ticket_id: "", expert_id: "", amount: "", scheduled_for: "", note: "" });
      alert("Payout expert planifié ✔️");
    } else {
      alert(error.message || "Erreur insertion payout");
    }
  };

  const onCreateInvoice = async (e) => {
    e.preventDefault();
    const cents = moneyToCents(invoice.amount);
    if (!invoice.partner_id || !cents) return;

    const { error } = await supabase.from("partner_invoices").insert({
      partner_id: invoice.partner_id,
      description: invoice.description || "Facture partenaire",
      amount_cents: cents,
      currency: "EUR",
      status: "open",
      issued_at: new Date().toISOString(),
      due_date: invoice.due_date || null,
      meta: { ticket_id: invoice.ticket_id || null }
    });

    if (!error) {
      setInvoice({ ticket_id: "", partner_id: "", description: "", amount: "", due_date: "" });
      alert("Facture partenaire créée ✔️");
    } else {
      alert(error.message || "Erreur insertion facture");
    }
  };

  if (loading) return <div>Chargement…</div>;

  return (
    <div className="space-y-6">
      <div className="mb-2">
        <h2 className="text-2xl font-bold">🏦 Facturation professionnelle</h2>
        <p className="text-sm text-gray-600">
          Créez des <strong>payouts experts</strong> (virements) et des <strong>factures partenaires</strong> liées à vos tickets.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Payout Expert */}
        <Box title="Virement expert (payout)">
          <form className="space-y-3" onSubmit={onCreatePayout}>
            <div>
              <label className="block text-sm font-medium mb-1">Ticket (optionnel)</label>
              <select
                value={payout.ticket_id}
                onChange={(e) => setPayout((s) => ({ ...s, ticket_id: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="">— lier à un ticket —</option>
                {estTickets.map((t) => (
                  <option key={t.id} value={t.id}>
                    [{t.kind}] {t.subject} — {new Date(t.created_at).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Expert</label>
              <select
                value={payout.expert_id}
                onChange={(e) => setPayout((s) => ({ ...s, expert_id: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2"
                required
              >
                <option value="">— choisir expert —</option>
                {experts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.first_name || ""} {p.last_name || ""} {p.company ? `— ${p.company}` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Montant (€)</label>
                <input
                  value={payout.amount}
                  onChange={(e) => setPayout((s) => ({ ...s, amount: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="ex: 450"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Date prévue (optionnel)</label>
                <input
                  type="date"
                  value={payout.scheduled_for}
                  onChange={(e) => setPayout((s) => ({ ...s, scheduled_for: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Note (optionnel)</label>
              <input
                value={payout.note}
                onChange={(e) => setPayout((s) => ({ ...s, note: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Contexte, référence interne…"
              />
            </div>

            <button className="px-4 py-2 rounded-lg bg-black text-white">Planifier le virement</button>
          </form>
        </Box>

        {/* Facture Partenaire */}
        <Box title="Facture partenaire (transport / assurance)">
          <form className="space-y-3" onSubmit={onCreateInvoice}>
            <div>
              <label className="block text-sm font-medium mb-1">Ticket lié (optionnel)</label>
              <select
                value={invoice.ticket_id}
                onChange={(e) => setInvoice((s) => ({ ...s, ticket_id: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2"
              >
                <option value="">— lier à un ticket —</option>
                {partnerTickets.map((t) => (
                  <option key={t.id} value={t.id}>
                    [{t.kind}] {t.subject} — {new Date(t.created_at).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Partenaire</label>
              <select
                value={invoice.partner_id}
                onChange={(e) => setInvoice((s) => ({ ...s, partner_id: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2"
                required
              >
                <option value="">— choisir partenaire —</option>
                {partners.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.first_name || ""} {p.last_name || ""} {p.company ? `— ${p.company}` : ""} ({p.partner_type})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <input
                value={invoice.description}
                onChange={(e) => setInvoice((s) => ({ ...s, description: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2"
                placeholder="Commission Artkiv — Transport Paris→Genève"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Montant (€)</label>
                <input
                  value={invoice.amount}
                  onChange={(e) => setInvoice((s) => ({ ...s, amount: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                  placeholder="ex: 120"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Échéance</label>
                <input
                  type="date"
                  value={invoice.due_date}
                  onChange={(e) => setInvoice((s) => ({ ...s, due_date: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>
            </div>

            <button className="px-4 py-2 rounded-lg bg-black text-white">Créer la facture</button>
          </form>
        </Box>
      </div>
    </div>
  );
}
